import { Row, Col, Button, Stack, Container } from "react-bootstrap"
import ProfileImage from "../../components/ProfileImage"
import { useAuth } from "../../api/auth/store"
import { ReactComponent as EditIcon } from "../../svgs/edit.svg"
import { ReactComponent as CheckIcon } from "../../svgs/check.svg"
import { ReactComponent as CancelIcon } from "../../svgs/stop.svg"

import { useEffect, useState } from "react"
import { SessionDetails, fetchUserDevices, removeDevice, DeviceData, changeDeviceStatus, FetchDevicesResponse } from "../../api/auth/actions"
import { ERR_DEVICE_NOT_FOUND, ERR_DEVICE_USER_MISMATCH, OWNER_ROLE } from "../../constants"
import Loading from "../../components/Loading"
import { EditableLabel, EditableName } from "../../components/EditableControl"
import CStack from "../../components/CStack"
import Label from "../../components/Label"
import DropdownControl from "../../components/DropdownControl" // Import DropdownControl
import useUXStore from "../../api/ux/store"
import LabelControl from "../../components/LabelControl"
import MfaSettingsSection from "../../containers/Profile/MfaSettingsSection" // Import the new MFA settings component
import AccountSection from "../../containers/Profile/AccountSection"
import ChangePassword from "../../containers/Profile/ChangePassword"
import SecurityActivity from "../../containers/Profile/SecurityActivity"
import DeleteAccount from "../../containers/Profile/DeleteAccount"


const ProfilePage = () => {
    const { sessionDetails, setSessionDetails, uploadAvatar, signed } = useAuth(state => state)
    const { refreshDialog } = useUXStore(state => state)
    const [edit, setEdit] = useState(false)
    const [loadingAvatar, setLoadingAvatar] = useState(false)
    const [editDetails, setEditDetails] = useState<SessionDetails | null>(null)
    // Update state to hold the entire response including current_device_id
    const [deviceData, setDeviceData] = useState<FetchDevicesResponse | null>(null);
    const [loadingDevices, setLoadingDevices] = useState(false)

    useEffect(() => {
        if (editDetails === null && sessionDetails !== null)
            setEditDetails(sessionDetails)
    }, [sessionDetails])

    useEffect(() => {
        if (signed) fetchDevices();
    }, [signed]);

    const fetchDevices = async () => {
        setLoadingDevices(true);
        try {
            const response = await fetchUserDevices();
            // Set the entire response object to state
            setDeviceData(response);
        } catch (error) {
            console.error("Failed to fetch devices:", error);
            // TODO: Add error handling/toast
        } finally {
            setLoadingDevices(false);
        }
    };

    //console.log(sessionDetails)
    if (sessionDetails === null) return <Loading center />

    const onUploadAvatar = (img: File) => {
        setLoadingAvatar(true)
        uploadAvatar(img).finally(() => setLoadingAvatar(false))
    }
    const toggleEdit = () => {
        setEdit(!edit)
    }
    const onApply = () => {
        setEdit(false)
        setSessionDetails(editDetails!)
    }
    const onChanged = (id: string, val: string) => {
        const changed = { ...editDetails, [id]: val }
        setEditDetails(changed as SessionDetails)
    }

    const handleRemoveDevice = async (deviceId: string) => {
        refreshDialog({
            id: "confirm-remove-device",
            title: "Confirm Removal",
            content: "Are you sure you want to remove this device?",
            visible: true,
            confirmContent: "Remove",
            onConfirm: () => {
                try {
                    removeDevice(deviceId).then(_ => {
                        // Refresh the device list after successful removal
                        fetchDevices();
                        refreshDialog({ id: "confirm-remove-device", visible: false });
                    });

                } catch (error: any) {
                    console.error("Failed to remove device:", error);
                    let errorMessage = "Failed to remove device";
                    if (error.message) {
                        if (error.message.includes(ERR_DEVICE_NOT_FOUND)) {
                            errorMessage = "Device not found";
                        } else if (error.message.includes(ERR_DEVICE_USER_MISMATCH)) {
                            errorMessage = "You don't have permission to remove this device";
                        } else {
                            errorMessage = error.message;
                        }
                    }
                    refreshDialog({
                        id: "device-remove-error",
                        title: "Error",
                        content: errorMessage,
                        visible: true,
                        onClose: () => refreshDialog({ id: "device-remove-error", visible: false }),
                    }, true);
                }
            },
            onClose: () => refreshDialog({ id: "confirm-remove-device", visible: false }),
        }, true);
    };

    const handleStatusChange = async (deviceId: string, newStatus: string) => {
        const dialogContent = newStatus === "blocked"
            ? "Blocking this device will prevent it from logging in and accessing resources. Are you sure you want to block this device?"
            : "Activating this device will allow it to log in and access resources. Are you sure you want to activate this device?";

        refreshDialog({
            id: "confirm-status-change",
            title: "Confirm Status Change",
            content: dialogContent,
            visible: true,
            confirmContent: "Confirm",
            onConfirm: () => {
                try {
                    changeDeviceStatus(deviceId, newStatus).then(_ => {
                        // Refresh the device list after successful status change
                        fetchDevices();
                    })
                } catch (error) {
                    console.error("Failed to change device status:", error);
                    // TODO: Add error handling/toast
                }
            },
            onClose: () => refreshDialog({ id: "confirm-status-change", visible: false }),
        }, true);
    };

    const deviceDropdownItems = ["active", "blocked", "Remove Device"];

    const handleChangePassword = () => {
        // Show the change password dialog
        refreshDialog({
            id: 'change-password-dialog',
            visible: true,
            content: <ChangePassword dialogId="change-password-dialog" />,
            title: '', // Title will be set by the ChangePassword component
            onClose: () => refreshDialog({ id: 'change-password-dialog', visible: false })
        });
    };

    const handleViewSecurityActivity = () => {
    // Show the security activity dialog
    refreshDialog({
      id: 'security-activity-dialog',
      visible: true,
      content: <SecurityActivity dialogId="security-activity-dialog" />,
      title: 'Security Activity',
      onClose: () => refreshDialog({ id: 'security-activity-dialog', visible: false })
    });
  };

  const isOwner = sessionDetails?.roles?.includes(OWNER_ROLE) || false;

    const handleDeleteAccount = () => {
        // Show the delete account confirmation dialog
        refreshDialog({
            id: 'delete-account-dialog',
            visible: true,
            content: <DeleteAccount dialogId="delete-account-dialog" />,
            title: 'Delete My Account',
            onClose: () => refreshDialog({ id: 'delete-account-dialog', visible: false })
        });
    };

    return <div>
        <div className="control-container">
            {!edit && <Button size="sm" variant="link" style={{ float: "right" }} onClick={toggleEdit}><EditIcon stroke={"gray"} /></Button>}
            {edit && <Stack style={{ float: "right" }} direction="horizontal">
                <Button size="sm" variant="link" onClick={toggleEdit}><CancelIcon stroke="red" /></Button>
                <Button size="sm" variant="link" onClick={onApply}><CheckIcon stroke="green" /></Button>
            </Stack>}
            <Container>
                <Row style={{ padding: 15 }}>
                    <Col sm="auto" style={{ padding: "0 25px 0 0" }}>
                        <div>
                            <ProfileImage loading={loadingAvatar} editable src={sessionDetails?.avatar} name={sessionDetails?.dis} size={80} bgcolor="rgb(217, 217, 217)" onChanged=
                                {onUploadAvatar} />
                        </div>
                    </Col>
                    <Col >
                        <EditableLabel edit={edit} label="Display" value={editDetails?.dis} onChanged={(val) => onChanged("dis", val)} />
                        <EditableName edit={edit} label="Name"
                            fname={editDetails?.fname} lname={editDetails?.lname}
                            onChangedFname={(val) => onChanged("fname", val)}
                            onChangedLname={(val) => onChanged("lname", val)} />
                        <EditableLabel label="Email" value={editDetails?.email} />
                    </Col>
                </Row>
            </Container>
        </div>
        <CStack header="Devices" padding="15px 0" onReload={fetchDevices}>
            {loadingDevices && <Loading center />}
            {/* Access devices and current_device_id from deviceData state */}
            {deviceData && deviceData.devices && deviceData.devices.length > 0 ? (
                deviceData.devices.map((device: DeviceData) => {
                    const isCurrentDevice = deviceData.current_device_id === device.id;
                    const deviceText = `${device.name} (${device.type})${isCurrentDevice ? ' (In-use)' : ''}`;
                    // Use Unix timestamp if available, otherwise fall back to ISO string
                    const lastAccessedDate = device.last_accessed_unix
                        ? new Date(device.last_accessed_unix * 1000) // Unix timestamp is in seconds
                        : new Date(device.last_accessed);
                    const deviceCaption = `Last accessed: ${lastAccessedDate.toLocaleString()}`;
                    const deviceTextColor = isCurrentDevice ? "green" : "";

                    if (isCurrentDevice) {
                        // Render BaseControl for the currently used device
                        return (
                            <LabelControl
                                key={device.id}
                                textColor={deviceTextColor}
                                text={deviceText}
                                caption={deviceCaption}
                                value={device.status}
                            />
                        );
                    } else {
                        // Render DropdownControl for other devices
                        return (
                            <DropdownControl
                                key={device.id}
                                textColor={deviceTextColor}
                                text={deviceText}
                                caption={deviceCaption}
                                items={deviceDropdownItems}
                                selected={deviceDropdownItems.indexOf(device.status)}
                                colors={["", "gray", "red"]}
                                onSelected={(item) => {
                                    if (item === "Remove Device") {
                                        handleRemoveDevice(device.id);
                                    } else if (item !== null) { // Ensure item is not null
                                        handleStatusChange(device.id, item);
                                    }
                                }}
                            />
                        );
                    }
                })
            ) : (
                !loadingDevices && <Label>No devices added.</Label>
            )}
        </CStack>

        {/* New MFA Settings Section */}
        <MfaSettingsSection />

        {/* Account Security Section */}
        <AccountSection
            onChangePassword={handleChangePassword}
            onViewSecurityActivity={handleViewSecurityActivity}
            onDeleteAccount={handleDeleteAccount}
            isOwner={isOwner}
        />
    </div>
}

export default ProfilePage
